import React, { useState } from "react"

import { NotificationManager } from "react-notifications"

import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid"

import { gql, useClient, useMutation } from "hooks/urql"

import { AcrClassPrompt } from "components/Pages/Teacher/AudioRooms/TeacherPrompts/types"

type StudentPromptCardProps = {
  prompt: AcrClassPrompt
  onSubmitted: () => void
}

const StudentPromptCard: React.FC<StudentPromptCardProps> = ({
  prompt,
  onSubmitted,
}) => {
  const client = useClient()
  const [answer, setAnswer] = useState("")
  const [mutationState, runMutation]: any[] = useMutation(
    submitPromptResponseMutation,
    client
  )

  const submit = (submittedAnswer: string) => {
    runMutation({ promptId: prompt.id, answer: submittedAnswer })
      .then(result => {
        const { failures } = result.data.submitPromptResponse
        if (failures.length > 0) {
          NotificationManager.error(failures[0].message)
          return
        }

        NotificationManager.success("Answer submitted")
        onSubmitted()
      })
      .catch(() => NotificationManager.error("Unable to submit answer"))
  }

  return (
    <section className="m-4 mb-0 flex-none rounded-xl border border-solid border-neutral-500 bg-neutral-700 p-4 lg:w-1/2">
      <p className="mb-1 text-sm text-neutral-300">
        Your teacher has asked you a question:
      </p>
      <p className="mb-3 text-lg font-semibold text-white">{prompt.body}</p>
      {prompt.answerType === "yesNo" ? (
        <div className="flex gap-3">
          <button
            type="button"
            disabled={mutationState.fetching}
            onClick={() => submit("yes")}
            className="flex appearance-none items-center gap-2 rounded-lg border-0 bg-emerald-600 bg-none px-8 py-3 font-semibold text-white shadow-none outline-none hover:bg-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-300 disabled:opacity-60"
          >
            Yes
            <CheckIcon className="h-7 w-7" />
          </button>
          <button
            type="button"
            disabled={mutationState.fetching}
            onClick={() => submit("no")}
            className="flex appearance-none items-center gap-2 rounded-lg border-0 bg-rose-700 bg-none px-8 py-3 font-semibold text-white shadow-none outline-none hover:bg-rose-600 focus-visible:ring-2 focus-visible:ring-rose-300 disabled:opacity-60"
          >
            No
            <XMarkIcon className="h-7 w-7" />
          </button>
        </div>
      ) : (
        <div>
          <textarea
            value={answer}
            maxLength={500}
            rows={3}
            disabled={mutationState.fetching}
            onChange={event => setAnswer(event.target.value)}
            className="w-full rounded-lg border border-solid border-neutral-600 bg-neutral-800 p-3 text-white hover:border-white focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
            placeholder="Type your answer"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-neutral-400">
              {answer.length}/500
            </span>
            <button
              type="button"
              disabled={mutationState.fetching || answer.trim().length === 0}
              onClick={() => submit(answer)}
              className="appearance-none rounded-lg border-0 bg-sky-700 bg-none px-5 py-2 font-semibold text-white shadow-none outline-none hover:bg-sky-600 focus-visible:ring-2 focus-visible:ring-sky-300 disabled:opacity-60"
            >
              {mutationState.fetching ? "Submitting..." : "Submit answer"}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

const submitPromptResponseMutation = gql`
  mutation SubmitAcrClassPromptResponse($promptId: ID!, $answer: String!) {
    submitPromptResponse(promptId: $promptId, answer: $answer) {
      failures {
        message
      }
    }
  }
`

export default StudentPromptCard
